/**
 * Hook for loading privileged accounts for non-PerfectServe users
 */
import { useCallback, useRef, useState } from "react";
import env from "@perfectserve/env-ps";
import { useHttp } from "@perfectserve/http-ps";
/**
 * Hook for loading privileged accounts for non-PerfectServe users.
 *
 * @param params - Optional parameters for user context
 * @param params.isPerfectServeUser - Whether the user is a PerfectServe employee (if undefined, will read from window.PSX_USER_CONFIGURATION)
 * @param params.userIdentityId - The user's identity ID (if undefined, will read from window.PSX_USER_CONFIGURATION)
 * @param params.permissions - The user's permissions array (if undefined, will read from window.PSX_USER_CONFIGURATION)
 */
export const usePrivilegedAccounts = (params = {}) => {
    const { get } = useHttp();
    // Use ref to store the get function to avoid dependency issues
    const getRef = useRef(get);
    getRef.current = get;
    const [privilegedAccounts, setPrivilegedAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    // Get user context from params or fallback to window.PSX_USER_CONFIGURATION
    const getUserContext = useCallback(() => {
        if (params.isPerfectServeUser !== undefined &&
            params.userIdentityId !== undefined &&
            params.permissions !== undefined) {
            // All params provided - use them directly
            return {
                isPerfectServeUser: params.isPerfectServeUser,
                userIdentityId: params.userIdentityId,
                permissions: params.permissions,
                permissionsLoaded: params.permissions.length > 0,
            };
        }
        // Fallback to window.PSX_USER_CONFIGURATION
        const config = typeof window !== "undefined" ? window.PSX_USER_CONFIGURATION : null;
        const permissions = config?.permissions || [];
        const isPerfectServeUser = Array.isArray(permissions) && permissions.includes("role://PerfectServe Employee");
        const permissionsLoaded = Array.isArray(permissions) && permissions.length > 0;
        return {
            isPerfectServeUser,
            userIdentityId: config?.identityId ?? null,
            permissions,
            permissionsLoaded,
        };
    }, [params.isPerfectServeUser, params.userIdentityId, params.permissions]);
    const userContext = getUserContext();
    const { isPerfectServeUser, userIdentityId, permissionsLoaded } = userContext;
    const loadPrivilegedAccounts = useCallback(async (identityId) => {
        if (!identityId) {
            setError("User identity ID is required");
            return;
        }
        // Get current user context to check if user is PerfectServe employee
        const currentContext = getUserContext();
        // PerfectServe users should not call this API - they use search instead
        if (currentContext.isPerfectServeUser) {
            setPrivilegedAccounts([]);
            return;
        }
        // Check cache first
        const cacheKey = `ps-privileged-accounts-${identityId}`;
        const accountDetailsCacheKey = `ps-account-details-${identityId}`;
        try {
            const cachedAccounts = sessionStorage.getItem(cacheKey);
            if (cachedAccounts) {
                const accounts = JSON.parse(cachedAccounts);
                setPrivilegedAccounts(accounts);
                return;
            }
        }
        catch {
            // Silently handle cache errors
        }
        setIsLoading(true);
        setError(null);
        try {
            // Step 1: Get context permissions from privileges API via gateway
            // Make parallel calls for both PracticeAccount and MidmarketAccount
            const gatewayBaseUrl = env.gatewayApi || "";
            const practiceAccountUrl = `/api/privileges/contexts/objectpermission?memberId=${identityId}&actionType=Read&objectType=PracticeAccount&contextType=PracticeAccount`;
            const midmarketAccountUrl = `/api/privileges/contexts/objectpermission?memberId=${identityId}&actionType=Read&objectType=MidmarketAccount&contextType=MidmarketAccount`;
            const [practiceAccountResponse, midmarketAccountResponse] = await Promise.all([
                getRef.current(practiceAccountUrl, { baseURL: gatewayBaseUrl }),
                getRef.current(midmarketAccountUrl, { baseURL: gatewayBaseUrl }),
            ]);
            // Combine results from both calls
            const practicePermissions = Array.isArray(practiceAccountResponse.data) ? practiceAccountResponse.data : [];
            const midmarketPermissions = Array.isArray(midmarketAccountResponse.data) ? midmarketAccountResponse.data : [];
            const contextPermissions = [...practicePermissions, ...midmarketPermissions];
            if (contextPermissions.length === 0) {
                setPrivilegedAccounts([]);
                setIsLoading(false);
                return;
            }
            // Step 2: Load cached account details if available
            let accountDetailsCache = {};
            try {
                const cachedDetails = sessionStorage.getItem(accountDetailsCacheKey);
                if (cachedDetails) {
                    accountDetailsCache = JSON.parse(cachedDetails);
                }
            }
            catch {
                // Silently handle cache errors
            }
            // Step 3: For each context permission, fetch full account details from CSP API
            const accountsMap = new Map();
            for (const permission of contextPermissions) {
                // Only process if objectId starts with "ACCT-"
                if (permission.objectId?.startsWith("ACCT-")) {
                    const accountId = permission.objectId;
                    // Skip if we already have this account
                    if (accountsMap.has(accountId)) {
                        continue;
                    }
                    // Check cache first
                    if (accountDetailsCache[accountId]) {
                        accountsMap.set(accountId, accountDetailsCache[accountId]);
                        continue;
                    }
                    // Step 4: Fetch full account details including timezone
                    try {
                        const accountResponse = await getRef.current(`/api/accounts/${accountId}`);
                        const accountData = accountResponse.data;
                        if (accountData && (accountData.Id || accountData.AccountId)) {
                            const mappedAccount = {
                                Id: accountData.Id || accountData.AccountId,
                                AccountName: accountData.AccountName || permission.name,
                                AccountType: accountData.AccountType || "Unknown",
                                Msa: accountData.Msa || "",
                                TimeZone: accountData.TimeZone || accountData.Timezone,
                            };
                            accountsMap.set(accountId, mappedAccount);
                            accountDetailsCache[accountId] = mappedAccount;
                        }
                    }
                    catch {
                        // If we can't fetch full account details, use context permission data
                        const fallbackAccount = {
                            Id: accountId,
                            AccountName: permission.name,
                            AccountType: "Unknown",
                            Msa: "",
                        };
                        accountsMap.set(accountId, fallbackAccount);
                        accountDetailsCache[accountId] = fallbackAccount;
                    }
                }
            }
            // Convert map to array and sort by account name
            const accountsArray = Array.from(accountsMap.values()).sort((a, b) => a.AccountName.localeCompare(b.AccountName));
            // Cache both the final accounts list and individual account details
            try {
                sessionStorage.setItem(cacheKey, JSON.stringify(accountsArray));
                sessionStorage.setItem(accountDetailsCacheKey, JSON.stringify(accountDetailsCache));
            }
            catch {
                // Silently handle cache errors
            }
            setPrivilegedAccounts(accountsArray);
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to load privileged accounts. Please contact support.";
            setError(errorMessage);
            setPrivilegedAccounts([]);
        }
        finally {
            setIsLoading(false);
        }
    }, [getUserContext]);
    return {
        privilegedAccounts,
        isLoading,
        error,
        loadPrivilegedAccounts,
        isPerfectServeUser,
        userIdentityId,
        permissionsLoaded,
    };
};
